/**
 * Notification utilities for push notifications and sounds
 */

// Notification sound URLs (using free sounds)
const NOTIFICATION_SOUNDS = {
  message: 'https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3',
  nearby: 'https://assets.mixkit.co/active_storage/sfx/1531/1531-preview.mp3',
  delivered: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
  newOrder: '/notification-order.mp3'
};

// Audio cache to avoid re-creating audio objects
const audioCache = {};
let audioUnlocked = false;

/**
 * Unlock audio playback on mobile browsers.
 * Must be called from a user gesture (tap/click) at least once.
 */
export const unlockAudio = () => {
  if (audioUnlocked) return;
  try {
    // Pre-create and warm up the newOrder audio (most important one)
    const audio = new Audio(NOTIFICATION_SOUNDS.newOrder);
    audio.volume = 0;
    audio.play().then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0.7;
      audioCache.newOrder = audio;
      audioUnlocked = true;
      console.log('Audio unlocked for notifications');
    }).catch(() => {});

    // Also try AudioContext unlock for broader compatibility
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
    ctx.resume().catch(() => {});
  } catch (e) {}
};

// Auto-unlock on first user interaction
if (typeof window !== 'undefined') {
  const onFirstInteraction = () => {
    unlockAudio();
    window.removeEventListener('touchstart', onFirstInteraction);
    window.removeEventListener('click', onFirstInteraction);
  };
  window.addEventListener('touchstart', onFirstInteraction, { once: true });
  window.addEventListener('click', onFirstInteraction, { once: true });
}

/**
 * Play a notification sound
 * @param {string} type - Type of notification: 'message', 'nearby', 'delivered', 'newOrder'
 */
export const playNotificationSound = (type = 'message') => {
  try {
    const soundUrl = NOTIFICATION_SOUNDS[type] || NOTIFICATION_SOUNDS.message;

    if (!audioCache[type]) {
      audioCache[type] = new Audio(soundUrl);
      audioCache[type].volume = type === 'newOrder' ? 0.7 : 0.5;
    }

    // Reset and play
    audioCache[type].currentTime = 0;
    audioCache[type].play().catch(err => {
      console.log('Could not play notification sound:', err.message);
      // Retry with a fresh Audio object (helps on some mobile browsers)
      try {
        const fresh = new Audio(soundUrl);
        fresh.volume = type === 'newOrder' ? 0.7 : 0.5;
        fresh.play().catch(() => {});
      } catch (e) {}
    });
  } catch (error) {
    console.error('Error playing notification sound:', error);
  }
};

/**
 * Request permission for browser notifications
 * @returns {Promise<boolean>} Whether permission was granted
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

/**
 * Show a browser push notification
 * @param {string} title - Notification title
 * @param {object} options - Notification options
 * @param {string} options.body - Notification body text
 * @param {string} options.icon - Icon URL
 * @param {string} options.tag - Unique tag to prevent duplicates
 * @param {boolean} options.playSound - Whether to play sound
 * @param {string} options.soundType - Type of sound to play
 */
export const showNotification = async (title, options = {}) => {
  const { 
    body = '', 
    icon = '/logo192.png',
    tag = 'todo-en-local',
    playSound = true,
    soundType = 'message'
  } = options;
  
  // Play sound if enabled
  if (playSound) {
    playNotificationSound(soundType);
  }
  
  // Try to show browser notification
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const notification = new Notification(title, {
        body,
        icon,
        tag,
        badge: '/logo192.png',
        vibrate: [200, 100, 200],
        requireInteraction: false
      });
      
      // Auto close after 5 seconds
      setTimeout(() => notification.close(), 5000);
      
      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }
  
  return null;
};

/**
 * Show notification for driver nearby
 * @param {string} driverName - Name of the driver
 * @param {number} eta - Estimated time of arrival in minutes
 */
export const notifyDriverNearby = (driverName, eta) => {
  return showNotification('🏍️ ¡Tu repartidor está cerca!', {
    body: `${driverName} llegará en aproximadamente ${eta} minutos`,
    soundType: 'nearby',
    tag: 'driver-nearby'
  });
};

/**
 * Show notification for order delivered
 * @param {string} orderNumber - Order number
 */
export const notifyOrderDelivered = (orderNumber) => {
  return showNotification('✅ ¡Pedido entregado!', {
    body: `Tu pedido ${orderNumber} ha sido entregado exitosamente`,
    soundType: 'delivered',
    tag: 'order-delivered'
  });
};

/**
 * Show notification for new chat message
 * @param {string} senderName - Name of the sender
 * @param {string} message - Message content
 */
export const notifyNewMessage = (senderName, message) => {
  return showNotification(`💬 Mensaje de ${senderName}`, {
    body: message.length > 50 ? message.substring(0, 50) + '...' : message,
    soundType: 'message',
    tag: 'chat-message'
  });
};

/**
 * Show notification for new order (for drivers)
 * @param {string} orderNumber - Order number
 * @param {string} address - Delivery address
 */
export const notifyNewOrder = (orderNumber, address) => {
  return showNotification('📦 ¡Nuevo pedido disponible!', {
    body: `Pedido ${orderNumber} - ${address}`,
    soundType: 'newOrder',
    tag: 'new-order'
  });
};

export default {
  playNotificationSound,
  requestNotificationPermission,
  showNotification,
  notifyDriverNearby,
  notifyOrderDelivered,
  notifyNewMessage,
  notifyNewOrder
};
